import type {
  ParsedWorkbook,
  RawRow,
  RawCellValue,
  SheetData,
  WorkbookMeta,
  ParserConfig,
} from "@/types/excel";
import { WorkbookParseError, ROW_INDEX_KEY, ROW_SHEET_KEY } from "@/types/excel";
import { t } from "@/i18n";
import * as XLSX from "xlsx";
import { evaluateFormula } from "./formulaEvaluator";

/**
 * Intelligent Header Row Detection heuristic.
 * Scans the first 10 rows and picks the row with the most non-empty text headers.
 */
export function detectHeaderRowIndex(rawRows: unknown[][]): number {
  if (!rawRows || rawRows.length === 0) return 0;

  let bestIndex = 0;
  let maxHeaderCount = 0;

  const scanLimit = Math.min(rawRows.length, 10);

  for (let i = 0; i < scanLimit; i++) {
    const row = rawRows[i];
    if (!Array.isArray(row)) continue;

    const count = row.filter(
      (cell) =>
        cell != null &&
        String(cell).trim() !== "" &&
        !String(cell).startsWith("=") &&
        isNaN(Number(cell)) // Header cells are typically text labels
    ).length;

    if (count > maxHeaderCount) {
      maxHeaderCount = count;
      bestIndex = i;
    }
  }

  return maxHeaderCount >= 2 ? bestIndex : 0;
}

/**
 * Convert a File object into a fully parsed ParsedWorkbook.
 * Extracts ALL sheets, ALL columns, and ALL rows.
 */
export async function parseWorkbook(
  file: File,
  config: ParserConfig = {}
): Promise<ParsedWorkbook> {
  const { onProgress } = config;

  onProgress?.(5);

  const arrayBuffer = await file.arrayBuffer();
  onProgress?.(30);

  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(arrayBuffer, {
      type: "array",
      cellFormula: true,
      cellNF: true,
      cellDates: true,
      cellStyles: true,
    });
  } catch {
    throw new WorkbookParseError(
      t("parser.couldNotRead"),
      "INVALID_FORMAT"
    );
  }

  onProgress?.(50);

  if (workbook.SheetNames.length === 0) {
    throw new WorkbookParseError(t("parser.noSheets"), "NO_SHEETS");
  }

  // 1. Read raw sheet data for all sheets
  const sheets: SheetData[] = workbook.SheetNames.map((name) => {
    const ws = workbook.Sheets[name];
    return readSheetData(ws, name);
  });

  // 2. Perform formula resolution pass across all sheets (VLOOKUP / XLOOKUP fallback)
  for (const sheet of sheets) {
    for (const row of sheet.rows) {
      for (const [colKey, rawVal] of Object.entries(row)) {
        if (typeof rawVal === "string" && rawVal.startsWith("=")) {
          const evaluated = evaluateFormula(rawVal, { sheets, currentRow: row });
          if (evaluated != null) {
            row[colKey] = evaluated;
          }
        }
      }
    }
  }

  onProgress?.(80);

  const meta: WorkbookMeta = {
    sheetCount: sheets.length,
    totalRows: sheets.reduce((sum, s) => sum + s.rows.length, 0),
    author: workbook.Props?.Author,
    created: workbook.Props?.CreatedDate?.toISOString(),
    modified: workbook.Props?.ModifiedDate?.toISOString(),
  };

  onProgress?.(100);

  return { name: workbook.SheetNames[0], sheets, meta };
}

/**
 * Read a single worksheet into SheetData with header detection & cached formula value extraction.
 */
export function readSheetData(
  ws: XLSX.WorkSheet,
  name: string
): SheetData {
  if (!ws || !ws["!ref"]) {
    return { name, headers: [], rows: [] };
  }

  const range = XLSX.utils.decode_range(ws["!ref"]);
  const rawRows: unknown[][] = [];

  for (let R = range.s.r; R <= range.e.r; ++R) {
    const row: unknown[] = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellAddress];

      if (!cell) {
        row.push(null);
        continue;
      }

      let val: RawCellValue = readCellValue(cell);

      // Priority 2: If cell has formula and raw value is empty/formula string, preserve formula for evaluation
      if (cell.f && (val == null || String(val).startsWith("="))) {
        val = `=${cell.f}`;
      }

      row.push(val);
    }
    rawRows.push(row);
  }

  if (rawRows.length === 0) {
    return { name, headers: [], rows: [] };
  }

  // Detect header row index
  const headerRowIndex = detectHeaderRowIndex(rawRows);
  const rawHeaders = rawRows[headerRowIndex] ?? [];

  // Normalize headers to unique strings
  const seen = new Set<string>();
  const headers: string[] = rawHeaders.map((h, i) => {
    const str = h != null && String(h).trim() !== "" ? String(h).trim() : `Cột ${i + 1}`;
    let unique = str;
    let counter = 1;
    while (seen.has(unique)) {
      unique = `${str}_${counter++}`;
    }
    seen.add(unique);
    return unique;
  });

  // Data rows are rows below header row
  const dataRows = rawRows.slice(headerRowIndex + 1);
  // Excel row number (1-based) of the first data row.
  const firstDataExcelRow = range.s.r + headerRowIndex + 2;

  const rows: RawRow[] = [];
  dataRows.forEach((row, offset) => {
    if (!row.some((cell) => cell != null && String(cell).trim() !== "")) return;
    const record: RawRow = {};
    for (let i = 0; i < headers.length; i++) {
      record[headers[i]] = (row[i] as RawCellValue) ?? null;
    }
    record[ROW_SHEET_KEY] = name;
    record[ROW_INDEX_KEY] = firstDataExcelRow + offset;
    rows.push(record);
  });

  return { name, headers, rows };
}

/**
 * Prefer the real calendar date over Excel's locale display (`cell.w`).
 * US Excel stores 29 Jul 2026 as serial/Date but prints `7/29/26` in `w`.
 */
function readCellValue(cell: XLSX.CellObject): RawCellValue {
  if (cell.t === "d" || cell.v instanceof Date) {
    const date = cell.v instanceof Date ? cell.v : cell.v != null ? new Date(cell.v as string | number) : null;
    const iso = date ? excelDateToIso(date) : null;
    if (iso) return iso;
  }

  if (cell.t === "n" && typeof cell.v === "number" && isExcelDateNumberFormat(cell.z)) {
    return cell.v;
  }

  if (cell.w != null) {
    return cell.w;
  }
  if (typeof cell.v === "string" || typeof cell.v === "number" || typeof cell.v === "boolean") {
    return cell.v;
  }
  if (cell.v != null) {
    return String(cell.v);
  }
  return null;
}

function isExcelDateNumberFormat(fmt: unknown): boolean {
  if (typeof fmt !== "string" || !fmt) return false;
  const f = fmt.toLowerCase();
  return /y{2,4}|d{1,2}|mmm/.test(f);
}

/** Calendar Y-M-D as ISO, avoiding locale short dates like 7/29/26. */
function excelDateToIso(d: Date): string | null {
  if (isNaN(d.getTime())) return null;
  const useLocal = d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
  const y = useLocal ? d.getFullYear() : d.getUTCFullYear();
  const m = (useLocal ? d.getMonth() : d.getUTCMonth()) + 1;
  const day = useLocal ? d.getDate() : d.getUTCDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function getSheetNames(workbook: ParsedWorkbook): string[] {
  return workbook.sheets.map((s) => s.name);
}

export function getSheet(
  workbook: ParsedWorkbook,
  sheetName: string
): SheetData | undefined {
  return workbook.sheets.find(
    (s) => s.name.toLowerCase() === sheetName.toLowerCase()
  );
}

export function sampleRows(sheet: SheetData, count = 10): RawRow[] {
  return sheet.rows.slice(0, count);
}

export function countDataRows(workbook: ParsedWorkbook): number {
  return workbook.meta.totalRows;
}

export { WorkbookParseError } from "@/types/excel";